// Localização: ./src/pages/Management/UsuariosPage.js (Versão 4.1.0 - SearchContext)

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSearch } from '../../context/SearchContext';
import api from '../../api/api'; 
import UserModal from '../../components/Management/UserModal'; 
import PageTitle from '../../components/Layout/PageTitle';
import PrimaryButton from '../../components/Common/PrimaryButton';
// Importando os ícones do Lucide
import { Users, SquarePen, Trash2, Mail, Clock, Shield, FileText, UserPlus } from 'lucide-react'; 
import { getUsuarioFotoUrl } from '../../utils/imageUtils';

// URL base para usuários
const API_URL = '/usuarios';

// Define a URL do avatar de fallback
const FALLBACK_AVATAR = '/images/higeia-avatar-shield.png';

// Componente de Card do Usuário
const UsuarioCard = ({ usuario, onEdit, onDelete, onViewProntuario }) => {
    const nomeCompleto = usuario.nome_completo || usuario.nome || 'Usuário';
    
    // Obter iniciais para avatar padrão
    const getIniciais = () => {
        if (nomeCompleto) {
            const partes = nomeCompleto.trim().split(' ');
            if (partes.length >= 2) {
                return `${partes[0][0]}${partes[partes.length - 1][0]}`.toUpperCase();
            }
            return partes[0][0].toUpperCase();
        }
        return 'U';
    };

    // Obter cor do role
    const getRoleColor = (role) => {
        const colors = {
            'proprietario': 'bg-emerald-100 text-emerald-800',
            'admin': 'bg-emerald-200 text-emerald-900',
            'gerente': 'bg-yellow-100 text-yellow-800',
            'enfermeiro': 'bg-blue-100 text-blue-800',
            'medico': 'bg-purple-100 text-purple-800',
            'fisioterapeuta': 'bg-teal-100 text-teal-800',
            'cuidador': 'bg-gray-100 text-gray-800',
        };
        return colors[role] || 'bg-gray-100 text-gray-800';
    };

    // Obter label do role
    const getRoleLabel = (role) => {
        const labels = {
            'proprietario': 'Proprietário',
            'admin': 'Administrador',
            'gerente': 'Gerente',
            'enfermeiro': 'Enfermeiro',
            'medico': 'Médico',
            'fisioterapeuta': 'Fisioterapeuta',
            'cuidador': 'Cuidador',
        };
        return labels[role] || role;
    };

    const fotoUrl = usuario.fotoURL ? getUsuarioFotoUrl(usuario.fotoURL) : null;

    return (
        <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-emerald-400 group">
            {/* Header do Card com Foto */}
            <div className="relative bg-gradient-to-br from-emerald-50 to-emerald-100 p-2 md:p-4">
                <div className="flex flex-col md:flex-row items-center md:items-start md:space-x-3 space-y-2 md:space-y-0">
                    {/* Foto do Usuário */}
                    <div className="flex-shrink-0">
                        <div className="relative">
                            {fotoUrl ? (
                                <img 
                                    className="h-12 w-12 md:h-16 md:w-16 rounded-full object-cover border-2 md:border-4 border-white shadow-md group-hover:scale-105 transition-transform duration-300" 
                                    src={fotoUrl} 
                                    alt={`Foto de ${nomeCompleto}`} 
                                    onError={(e) => {
                                        e.target.onerror = null; 
                                        e.target.src = FALLBACK_AVATAR; 
                                    }}
                                />
                            ) : (
                                <div className="h-12 w-12 md:h-16 md:w-16 rounded-full border-2 md:border-4 border-white shadow-md bg-higeia-dark flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                                    <span className="text-lg md:text-2xl font-bold text-white">
                                        {getIniciais()}
                                    </span>
                                </div>
                            )}
                            {/* Status Badge (Ativo/Inativo) */}
                            <div className={`absolute -bottom-0.5 -right-0.5 md:-bottom-1 md:-right-1 h-3.5 w-3.5 md:h-5 md:w-5 rounded-full border-2 border-white ${
                                usuario.ativo !== false ? 'bg-green-500' : 'bg-red-500'
                            }`} title={usuario.ativo !== false ? 'Ativo' : 'Inativo'}></div>
                        </div>
                    </div>
                    
                    {/* Nome e Role */}
                    <div className="flex-1 min-w-0 text-center md:text-left w-full md:w-auto">
                        <h3 className="text-sm md:text-lg font-bold text-gray-900 truncate group-hover:text-emerald-700 transition-colors">
                            {nomeCompleto}
                        </h3>
                        <p className="text-xs text-gray-600 truncate mt-0.5 md:mt-1">
                            {getRoleLabel(usuario.role)}
                        </p>
                    </div>
                </div>
            </div>

            {/* Corpo do Card */}
            <div className="p-2 md:p-4 space-y-2 md:space-y-3">
                {/* Informações */}
                <div className="space-y-1.5 md:space-y-2">
                    {/* Email */}
                    <div className="flex items-center text-xs md:text-sm text-gray-600">
                        <Mail className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2 text-emerald-600 flex-shrink-0" />
                        <span className="truncate">
                            {usuario.email || 'Não informado'}
                        </span>
                    </div>

                    {/* Último Acesso */}
                    {usuario.last_login && (
                        <div className="flex items-center text-xs md:text-sm text-gray-600">
                            <Clock className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2 text-emerald-600 flex-shrink-0" />
                            <span className="truncate">
                                {new Date(usuario.last_login).toLocaleDateString('pt-BR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                })}
                            </span>
                        </div>
                    )}

                    {/* Role Badge */}
                    <div className="flex items-center justify-center md:justify-start pt-1">
                        <span className={`px-2 md:px-3 py-0.5 md:py-1 inline-flex text-xs font-semibold rounded-full ${getRoleColor(usuario.role)}`}>
                            <Shield className="h-3 w-3 mr-1" />
                            {getRoleLabel(usuario.role)}
                        </span>
                    </div>
                </div>

                {/* Ações */}
                <div className="flex items-center justify-center md:justify-end gap-1 md:gap-2 pt-2 md:pt-3 border-t border-gray-200">
                    {/* Botão Prontuário (apenas para profissionais) */}
                    {onViewProntuario && ['cuidador', 'enfermeiro', 'medico', 'fisioterapeuta', 'gerente'].includes(usuario.role?.toLowerCase()) && (
                        <button
                            onClick={() => onViewProntuario(usuario.id)}
                            className="p-1.5 md:p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors duration-200"
                            title="Ver Prontuário"
                        >
                            <FileText className="w-4 h-4 md:w-5 md:h-5" />
                        </button>
                    )}
                    
                    {/* Botão Editar */}
                    <button
                        onClick={() => onEdit(usuario)}
                        className="p-1.5 md:p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors duration-200"
                        title="Editar"
                    >
                        <SquarePen className="w-4 h-4 md:w-5 md:h-5" />
                    </button>
                    
                    {/* Botão Excluir/Desativar */}
                    <button
                        onClick={() => onDelete(usuario.id, nomeCompleto)}
                        className="p-1.5 md:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        title="Desativar"
                    >
                        <Trash2 className="w-4 h-4 md:w-5 md:h-5" />
                    </button>
                </div>
            </div>
        </div>
    );
};

// ----------------------------------------------------
// Componente principal (UsuariosPage)
// ----------------------------------------------------
const UsuariosPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Estado para o Modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null); 
    
    // 🎯 USANDO SEARCHCONTEXT: Substituir estado local por contexto global
    const { searchTerm, setSearchPlaceholder } = useSearch();
    const [pagina, setPagina] = useState(1);
    const itensPorPagina = 10;

    // Configurar o placeholder de busca para esta página
    useEffect(() => {
        setSearchPlaceholder('Buscar por nome, email ou permissão...');
    }, [setSearchPlaceholder]);

    // Reset para primeira página quando o termo de busca mudar
    useEffect(() => {
        setPagina(1);
    }, [searchTerm]); 

    // FUNÇÃO DE BUSCA DE DADOS (fetchUsuarios)
    const fetchUsuarios = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get(API_URL, {
                params: {
                    page: pagina,
                    limit: itensPorPagina,
                    search: searchTerm
                }
            });

            setUsuarios(response.data);
        } catch (err) {
            console.error('Erro ao buscar usuários:', err);
            const errorMsg = err.response?.data?.message || 'Falha ao carregar a lista de usuários.';
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    }, [pagina, searchTerm]);

    useEffect(() => {
        fetchUsuarios();
    }, [fetchUsuarios]);

    // 🎯 Filtro local (para busca em tempo real usando SearchContext)
    const filteredUsuarios = useMemo(() => {
        if (!searchTerm) {
            return usuarios;
        }

        const lowerCaseSearch = searchTerm.toLowerCase();
        return usuarios.filter(u => 
            (u.nome_completo || u.nome || '').toLowerCase().includes(lowerCaseSearch) ||
            (u.email || '').toLowerCase().includes(lowerCaseSearch) ||
            (u.role || '').toLowerCase().includes(lowerCaseSearch)
        );
    }, [usuarios, searchTerm]);

    // HANDLERS PARA O MODAL
    const handleOpenEdit = (userToEdit) => {
        setEditingUser(userToEdit);
        setIsModalOpen(true);
    };

    const handleOpenCreate = () => {
        setEditingUser(null);
        setIsModalOpen(true);
    };

    const handleSaveSuccess = () => {
        setIsModalOpen(false); 
        setEditingUser(null);
        fetchUsuarios(); 
    };

    // HANDLER PARA VER PRONTUÁRIO DO PROFISSIONAL
    const handleViewProntuario = (userId) => {
        navigate(`/profissionais/${userId}/prontuario`);
    };

    // HANDLER DE EXCLUSÃO (SOFT DELETE + DESVINCULAÇÃO)
    const handleDeleteUser = async (userId, userName) => {
        if (!window.confirm(`Tem certeza que deseja DESATIVAR o usuário "${userName}"? Isso é um soft-delete e ele não aparecerá mais nesta lista.`)) {
            return;
        }

        setLoading(true); 
        try {
            await api.delete(`${API_URL}/${userId}`);
            
            alert(`Usuário "${userName}" desativado com sucesso!`); 

            fetchUsuarios(); 

        } catch (error) {
            console.error('Falha ao desativar usuário:', error.response || error);
            alert('Erro ao desativar usuário: ' + (error.response?.data?.message || 'Erro desconhecido.'));
            setLoading(false);
        }
    };
    
    // RENDERIZAÇÃO
    if (loading && usuarios.length === 0) {
        return (
            <div className="p-4 sm:p-6 lg:p-8 min-h-screen">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                        <p className="text-emerald-600 font-semibold text-lg">Carregando usuários...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8 min-h-screen"> 
            <div className="max-w-7xl mx-auto">
                {/* Header com Design Aprimorado */}
                <PageTitle 
                    title="Gestão de Usuários"
                    subtitle="Gerencie usuários, permissões e acessos da sua organização"
                >
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <PrimaryButton
                            onClick={handleOpenCreate}
                            icon={<UserPlus className="w-5 h-5" />}
                            variant="primary"
                            size="md"
                            className="w-full sm:w-auto"
                        >
                            Criar Usuário
                        </PrimaryButton>
                    </div>
                </PageTitle>
                
                {/* 🎯 Barra de Pesquisa removida - Agora está no HeaderGestão */}

                {/* Feedback de Estado */}
                {loading && usuarios.length > 0 && (
                    <div className="text-center py-4 bg-white rounded-lg shadow-lg mb-6">
                        <p className="text-emerald-600 font-semibold">Atualizando lista...</p>
                    </div>
                )}
                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative shadow-md mb-6" role="alert">
                        <strong className="font-bold">Erro de Comunicação:</strong>
                        <span className="block sm:inline"> {error}</span>
                        <p className="text-sm mt-1">Verifique se o seu servidor (Backend) está rodando.</p>
                    </div>
                )}

                {/* Grid de Cards - Responsivo: 2 colunas no mobile (matriz 2x2), 4 colunas no desktop/tablet */}
                {!loading && !error && (
                    filteredUsuarios.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
                            {filteredUsuarios.map(usuario => (
                                <UsuarioCard
                                    key={usuario.id}
                                    usuario={usuario}
                                    onEdit={handleOpenEdit}
                                    onDelete={handleDeleteUser}
                                    onViewProntuario={handleViewProntuario}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                            <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                            <p className="text-gray-500 text-lg">
                                {searchTerm 
                                    ? `Nenhum usuário encontrado para "${searchTerm}".` 
                                    : 'Nenhum usuário encontrado.'}
                            </p>
                        </div>
                    )
                )}
                
                {/* RENDERIZAÇÃO DO MODAL */}
                <UserModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSaveSuccess={handleSaveSuccess} 
                    editingUser={editingUser}
                />
            </div>
        </div>
    );
};

export default UsuariosPage;
